// src/data/claimDocumentUpload.js
//
// SECTION 412 PART A - THE CLAIM-DOCUMENT UPLOAD, MEDIATED BY A CALLABLE.
//
// THE GAP THIS CLOSES. storage.rules authorised a claim-document WRITE on
// `request.resource.metadata.createdBy == request.auth.uid` - metadata the UPLOADER sends - while the
// READ arm scopes on `resource.metadata.committee/scopeKey` already STORED. So a member could set
// createdBy AND uploadedByUid to his OWN uid but committee/scopeKey to a VICTIM's claim: the write
// landed (his own uid satisfied the createdBy branch) and the file then surfaced to the victim claim's
// officers, who read by committee/scopeKey. PROVED on the Storage emulator (section 412). Storage
// rules cannot read Firestore, so they cannot ask whether an uploader is a party to the claim.
//
// THE FIX. Client writes to claims/{claimId}/documents are turned OFF (storage.rules: allow write: if
// false). The ONLY writer is the claimDocumentUploadUrl Cloud Function, which reads claims/{claimId}
// with the Admin SDK, confirms the caller is a PARTY to THAT claim by mayAttachToClaim() below -
// mirroring the firestore.rules `match /claims/{claimId}` read arm exactly - and only then creates a
// resumable upload session whose metadata is baked in server-side (createdBy = the CLAIM's owner,
// committee/scopeKey from the claim, uploadedByUid = the caller). Because only the function sets the
// metadata, the READ rule that trusts that metadata is finally trustworthy.
//
// WHY A RESUMABLE SESSION AND NOT getSignedUrl (section 412 decision said "signed URL"; this is the
// same capability by the mechanism that is PROVABLE and correct - recorded as a ruling with its
// provenance). Two facts, both read off the Storage emulator on 2026-09-25, not reasoned:
//   1. bucket.file(p).getSignedUrl({action:'write'}) throws "Could not load the default credentials"
//      in the emulator - signing needs a service account / ADC - so PD-0 cannot prove its happy path
//      here at all, and a rule flipped on an unprovable upload path is the F-15 / R-3 disaster.
//   2. bucket.file(p).createResumableUpload({metadata:{metadata:{createdBy,...}}}) returns a session
//      URI (status 200 on the PUT) and stores the custom-metadata keys as EXACT camelCase -
//      createdBy, committee, scopeKey, uploadedByUid - which is what the read rule
//      (`resource.metadata.createdBy`) depends on. getSignedUrl carries metadata as client-sent
//      x-goog-meta-* headers instead; the resumable session bakes it server-side via the JSON API,
//      the same way submitApplication's roster exhibit already does in production.
// Both give a short-lived, membership-verified, single-object upload capability for a file up to 25MB
// (the exhibit ceiling, over an onCall payload ceiling - which is why the bytes cannot travel through
// the callable itself the way the 5MB roster exhibit does).
//
// PURE MODULE. No firebase, no I/O. It is the ONE source of the decision and the shapes, projected to
// functions/shared/claimDocumentUpload.js by scripts/sync-shared-modules.js so the callable and the
// browser decide with identical code (SS XI). Proved by scripts/test-claim-document-upload.mjs.

// The exhibit ceiling. One number, here, read by the callable and echoed by the browser field.
export const MAX_BYTES = 25 * 1024 * 1024;

// The document/image types the claim-document field accepts, matching ClaimDocuments.js's accept list
// (.pdf .doc .docx .jpg .jpeg .png .txt .xls .xlsx). Recordings (audio/*, video/*) are handled below
// by the ruling-157 origin gate rather than by this set.
export const ALLOWED_DOC_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'text/plain',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

function isRecordingType(contentType) {
  const t = String(contentType || '');
  return /^audio\//.test(t) || /^video\//.test(t);
}

// The same gate the callable runs, run in the browser first so the member is told at the field, not
// after a round trip. It is NOT a substitute for the server's - the callable runs it again on arrival.
// Returns { ok, why }.
export function validateClaimUpload(file) {
  const f = file || {};
  const contentType = String(f.contentType || '');
  const bytes = Number(f.bytes);

  if (!contentType) return { ok: false, why: 'That file has no type the platform can identify. Send a PDF, a Word or Excel file, an image, or a text file.' };

  if (isRecordingType(contentType)) {
    // RULING 157 (2026-09-17): a recording is STORED only when carrier-produced. Enforced here now
    // that the client write rule is off and cannot enforce recordingAllowed() itself.
    // Record 613 (Bob 10/8): the refusal points to where such a recording goes - the Recordings tracker, which is on the
    // claim page and the discipline case view (src\components\MediaTracker.js) - and says why a carrier recording would be
    // refused (the upload did not say it was carrier-produced).
    if (f.mediaOrigin !== 'carrier_produced') {
      return { ok: false, why: 'A recording is stored only when the carrier produced it, and this upload does not say it was carrier-produced. A recording made by the organization or by a member is not stored: list it in the Recordings tracker on this page - who made it, who holds it and where it is.' };   // Record 589 (audit), ruling 168: a member reads this; ruling 157's number is not printed in it
    }
  } else if (!ALLOWED_DOC_TYPES.includes(contentType)) {
    return { ok: false, why: 'That file type is not accepted. Send a PDF, a Word or Excel file, a JPG or PNG image, or a text file.' };
  }

  if (!isFinite(bytes) || bytes <= 0) return { ok: false, why: 'That file is empty, or its size could not be read.' };
  if (bytes > MAX_BYTES) {
    return { ok: false, why: 'That file is ' + (bytes / (1024 * 1024)).toFixed(1) + ' MB. The limit is 25 MB - send the page, not the book.' };
  }
  return { ok: true, why: '' };
}

// WHO MAY ATTACH A FILE TO THIS CLAIM. Mirrors firestore.rules `match /claims/{claimId}` READ arm
// EXACTLY (that is the arm the task names): owner always; admin always; a named claimant, an LC whose
// scopes hold the claim's scopeKey, and the GC of the claim's committee - each ONLY once the claim has
// left draft, exactly as `!isDraftDoc()` gates them there. A draft belongs to its author alone
// (ruling 56), so only its owner (and admin) may attach to it.
//
//   caller: { uid, role, committee, scopes[], email, emailVerified }
//   claim:  { createdBy, claimantUid, claimantEmail, scopeKey, committee, status }
//
// Returns { ok, as, reason }. `as` names the arm that allowed it, for the audit line.
export function mayAttachToClaim(caller, claim) {
  const c = caller || {};
  const k = claim || {};
  const role = c.role;
  const isDraft = k.status === 'draft';
  const low = (v) => String(v == null ? '' : v).trim().toLowerCase();

  if (role === 'admin') return { ok: true, as: 'admin', reason: '' };

  if (c.uid && k.createdBy && k.createdBy === c.uid) return { ok: true, as: 'owner', reason: '' };

  if (role === 'member' && !isDraft) {
    if (k.claimantUid && k.claimantUid === c.uid) return { ok: true, as: 'claimant', reason: '' };
    // Forensic audit 2026-10-16: the e-mail arm admitted an UNVERIFIED address - anyone who signed up with a member's
    // e-mail could attach to her claim. The read rule's arm requires email_verified (firestore.rules emailVerified(),
    // ruling 298); so does this. Compared lower-cased, as linkDecision compares it.
    if (c.emailVerified === true && low(k.claimantEmail) && low(c.email) && low(k.claimantEmail) === low(c.email)) {
      return { ok: true, as: 'claimant', reason: '' };
    }
  }

  if (!isDraft) {
    if (role === 'local_chair' && Array.isArray(c.scopes) && k.scopeKey && c.scopes.indexOf(k.scopeKey) !== -1) {
      return { ok: true, as: 'local_chair', reason: '' };
    }
    if (role === 'general_chair' && c.committee && k.committee && c.committee === k.committee) {
      return { ok: true, as: 'general_chair', reason: '' };
    }
  }

  if (isDraft) return { ok: false, as: null, reason: 'This claim is still a draft; only its author may attach a file to it.' };
  return { ok: false, as: null, reason: 'You are not a party to this claim, so you cannot attach a file to it.' };
}

// The read rule scopes on the object's committee AND scopeKey, so a claim missing either cannot carry
// a readable document. The callable refuses rather than plant an unreadable file.
export function claimFullyScoped(claim) {
  const k = claim || {};
  return !!(k.createdBy && k.committee && k.scopeKey);
}

// The name is never trusted: strip any path and reduce to a safe basename. Empty becomes 'document'.
export function safeClaimDocName(name) {
  const base = String(name || '').replace(/^.*[\\/]/, '').trim();
  const safe = base.replace(/[^a-zA-Z0-9._-]/g, '_');
  return safe || 'document';
}

// The object path. The same shape ClaimDocuments.js has always used, so nothing else has to change to
// read it: claims/{claimId}/documents/{timestamp}_{safeName}.
export function claimDocumentObjectPath(claimId, timestamp, safeName) {
  return 'claims/' + String(claimId) + '/documents/' + String(timestamp) + '_' + safeClaimDocName(safeName);
}

// The metadata the read rule trusts, built from the CLAIM (not from anything the client sent):
// createdBy is the claim's OWNER (the read rule scopes on it); uploadedByUid is the caller who did
// the upload; committee and scopeKey come off the claim. mediaOrigin is carried only for a recording.
export function claimUploadMetadata(claim, callerUid, mediaOrigin) {
  const k = claim || {};
  const md = {
    createdBy: String(k.createdBy || ''),
    committee: String(k.committee || ''),
    scopeKey: String(k.scopeKey || ''),
    uploadedByUid: String(callerUid || ''),
  };
  if (mediaOrigin) md.mediaOrigin = String(mediaOrigin);
  return md;
}
